import { apiClient } from '_app'
import {
  ApiResponse,
  CompanyAddressDto,
  CompanyAttorneyInformationDto,
  CompanyBuyerCompanyDto,
  CompanyClusterConversionCompanyDto,
  CompanyClusterProgrammerCompanyDto,
  CompanyDto,
  CompanyExporterInformationDto,
  CompanyInsuranceCompanyInformationDto,
  CompanyRatesDto,
  CompanyRegisteredImporterDto,
  CompanyTransporterInformationDto
} from 'dtos'
import { CompaniesParameters, CompanyAddressesParameters } from 'parameters'
import { URIQuery, base64ToArrayBuffer } from 'utils'

export const getCompanies = (parameters: CompaniesParameters) =>
  apiClient.get<ApiResponse<CompanyDto[]>>('/api/companies' + URIQuery({ ...parameters })).then(response => response.data)

export const getCompanyById = (id: string) =>
  apiClient.get<ApiResponse<CompanyDto>>('/api/companies/' + encodeURIComponent(id)).then(response => response.data)

export const getBrokerCompany = () =>
  apiClient.get<ApiResponse<CompanyDto | undefined>>('/api/companies/broker-company').then(response => response.data)

export const getRegisteredImporterCompany = () =>
  apiClient
    .get<ApiResponse<CompanyDto | undefined>>('/api/companies/registered-importer-company')
    .then(response => response.data)

export const createCompany = (values: CompanyDto) =>
  apiClient.post<ApiResponse<CompanyDto>>('/api/companies', values).then(response => response.data)

export const createCompanyAddress = (companyId: string, values: CompanyAddressDto) =>
  apiClient
    .post<ApiResponse<CompanyAddressDto>>(`/api/companies/${encodeURIComponent(companyId)}/addresses`, values)
    .then(response => response.data)

export const updateCompany = (values: CompanyDto) =>
  apiClient.put<ApiResponse<CompanyDto>>('/api/companies', values).then(response => response.data)

export const updateCompanyAddress = (companyId: string, values: CompanyAddressDto) =>
  apiClient
    .put<ApiResponse<CompanyAddressDto>>(`/api/companies/${encodeURIComponent(companyId)}/addresses`, values)
    .then(response => response.data)

export const getCompaniesAddresses = (parameters: CompanyAddressesParameters) =>
  apiClient
    .get<ApiResponse<CompanyAddressDto[]>>(`/api/companies/addresses` + URIQuery({ ...parameters }))
    .then(response => response.data)

export const getCompanyAddresses = (companyId: string, parameters: CompanyAddressesParameters) =>
  apiClient
    .get<ApiResponse<CompanyAddressDto[]>>(
      `/api/companies/${encodeURIComponent(companyId)}/addresses` + URIQuery({ ...parameters })
    )
    .then(response => response.data)

export const getBrokerCompanyAddress = () =>
  apiClient
    .get<ApiResponse<CompanyAddressDto | undefined>>('/api/companies/addresses/broker-company-address')
    .then(response => response.data)

export const getRegisteredImporterAddress = () =>
  apiClient
    .get<ApiResponse<CompanyAddressDto | undefined>>('/api/companies/addresses/registered-importer-company-address')
    .then(response => response.data)

export const getCompanyExporterInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyExporterInformationDto>>(`/api/companies/${encodeURIComponent(companyId)}/exporter-information`)
    .then(response => response.data)

export const updateCompanyExporterInformation = (companyId: string, values: CompanyExporterInformationDto) =>
  apiClient
    .put<ApiResponse<CompanyExporterInformationDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/exporter-information`,
      values
    )
    .then(response => response.data)

export const getCompanyTransporterInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyTransporterInformationDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/transporter-information`
    )
    .then(response => response.data)

export const updateCompanyTransporterInformation = (companyId: string, values: CompanyTransporterInformationDto) =>
  apiClient
    .put<ApiResponse<CompanyTransporterInformationDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/transporter-information`,
      values
    )
    .then(response => response.data)

export const getCompanyRegisteredImporter = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyRegisteredImporterDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/registered-importer-information`
    )
    .then(response => response.data)

export const updateCompanyRegisteredImporter = (companyId: string, values: CompanyRegisteredImporterDto) =>
  apiClient
    .put<ApiResponse<CompanyRegisteredImporterDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/registered-importer-information`,
      values
    )
    .then(response => response.data)

export const getCompanyAttorneyInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyAttorneyInformationDto>>(`/api/companies/${encodeURIComponent(companyId)}/attorney-information`)
    .then(response => response.data)

export const getCompanyAttorneyInformationSignatureFile = (companyId: string, fileName: string) =>
  apiClient.get(`/api/companies/${encodeURIComponent(companyId)}/attorney-information/${fileName}`).then(response => {
    const buffer = base64ToArrayBuffer(response.data)
    const file = new Blob([buffer], { type: 'image/png' })
    const fileUrl = window.URL.createObjectURL(file)
    const _newTab = window.open(fileUrl)

    // Uncomment below if it is supposed to download instead of open in a new tab.
    // const buffer = base64ToArrayBuffer(response.data)
    // const file = new Blob([buffer])
    // const anchor = document.createElement('a')
    // anchor.href = window.URL.createObjectURL(file)
    // anchor.download = fileName
    // document.body.appendChild(anchor)
    // anchor.click()
    // document.body.removeChild(anchor)
  })

export const updateCompanyAttorneyInformation = (companyId: string, values: CompanyAttorneyInformationDto) => {
  const payload = new FormData()

  payload.append('signatureFile', values.signatureFile!)

  return apiClient<ApiResponse<CompanyAttorneyInformationDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/companies/${encodeURIComponent(companyId)}/attorney-information`
  })
    .then(response => response.data)
    .catch(e => {
      throw e
    })
}

export const getCompanyInsuranceCompanyInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyInsuranceCompanyInformationDto>>(`/api/companies/${companyId}/insurance-company-information`)
    .then(response => response.data)

export const getCompanyInsuranceCompanyInformationSuretyImageSealFile = (companyId: string, fileName: string) =>
  apiClient.get(`/api/companies/${encodeURIComponent(companyId)}/insurance-company-information/${fileName}`).then(response => {
    const buffer = base64ToArrayBuffer(response.data)
    const file = new Blob([buffer], { type: 'image/png' })
    const fileUrl = window.URL.createObjectURL(file)
    const _newTab = window.open(fileUrl)
  })

export const updateCompanyInsuranceCompanyInformation = (companyId: string, values: CompanyInsuranceCompanyInformationDto) => {
  const payload = new FormData()

  const { insuranceSuretyImageSealFile, ...rest } = values

  payload.append('companyInsuranceCompanyInformationDto', JSON.stringify(rest))
  payload.append('insuranceSuretyImageSealFile', insuranceSuretyImageSealFile!)

  return apiClient<ApiResponse<CompanyInsuranceCompanyInformationDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/companies/${encodeURIComponent(companyId)}/insurance-company-information`
  }).then(response => response.data)
}

export const getCompanyBuyerInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyBuyerCompanyDto>>(`/api/companies/${encodeURIComponent(companyId)}/buyer-company-information`)
    .then(response => response.data)

export const updateCompanyBuyerCompanyInformation = (companyId: string, values: CompanyBuyerCompanyDto) =>
  apiClient
    .put<ApiResponse<CompanyBuyerCompanyDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/buyer-company-information`,
      values
    )
    .then(response => response.data)

export const getCompanyClusterConversionInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyClusterConversionCompanyDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/cluster-conversion-company-information`
    )
    .then(response => response.data)

export const updateCompanyClusterConversionCompanyInformation = (
  companyId: string,
  values: CompanyClusterConversionCompanyDto
) =>
  apiClient
    .put<ApiResponse<CompanyClusterConversionCompanyDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/cluster-conversion-company-information`,
      values
    )
    .then(response => response.data)

export const getCompanyClusterProgrammerInformation = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyClusterProgrammerCompanyDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/cluster-programmer-company-information`
    )
    .then(response => response.data)

export const updateCompanyClusterProgrammerCompanyInformation = (
  companyId: string,
  values: CompanyClusterProgrammerCompanyDto
) =>
  apiClient
    .put<ApiResponse<CompanyClusterProgrammerCompanyDto>>(
      `/api/companies/${encodeURIComponent(companyId)}/cluster-programmer-company-information`,
      values
    )
    .then(response => response.data)

export const getCompanyRates = (companyId: string) =>
  apiClient
    .get<ApiResponse<CompanyRatesDto>>(`/api/companies/${encodeURIComponent(companyId)}/rates`)
    .then(response => response.data)

export const updateCompanyRates = (companyId: string, values: CompanyRatesDto) =>
  apiClient
    .put<ApiResponse<CompanyRatesDto>>(`/api/companies/${encodeURIComponent(companyId)}/rates`, values)
    .then(response => response.data)
